import type { StrokeDto, Vec3 } from '../generated/contracts';
import { advanceFor, GLYPH_HEIGHT, glyphFor } from '../room/StrokeFont';

/**
 * The pure, Babylon-free half of the scan tool (`ScanTool.ts` is the Scene-dependent gesture
 * that drives this): which strokes a swept X range catches, the region those strokes actually
 * occupy, and how to lay recognised text back out as replacement strokes inside it. Kept
 * separate from `ScanTool.ts` for the same reason `WhiteboardLayout.ts` is kept separate from
 * `SceneRoot.ts` — a `NullEngine` proves nothing about pixels, but every claim here (which
 * strokes get caught, where the region's edges land, how many points a character's strokes
 * come out as) is a plain function of numbers in and numbers out, and is exactly as testable
 * without a Scene at all.
 */

/** The world-space rectangle (on the board's own plane, so a single `z`) a caught set of
 *  strokes actually occupies. */
export interface ScanRegion {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  /** The board's own z — every stroke on one whiteboard shares it (`WhiteboardLayout`, "only
   *  the north wall is ever the board"). */
  readonly z: number;
}

/** Floor under both of a region's own extents, so a degenerate one-point (or one-line) catch
 *  never divides by (near) zero when `layoutTextStrokes` derives a scale from it. 2cm — smaller
 *  than anything a real stroke's own bounding box is likely to be, so it only ever bites the
 *  genuinely degenerate case, not an ordinary short mark. */
const MIN_REGION_EXTENT = 0.02;

/**
 * Every stroke with **at least one point** inside the swept X range `[xEdgeA, xEdgeB]`
 * (order-independent — either argument may be the larger) — and, if `yRange` is given, that same
 * point also inside `[yRange[0], yRange[1]]` (also order-independent). A stroke earns a spot in
 * the result by having one point land inside the rectangle those two ranges describe; nothing
 * about a stroke that fails this on every one of its points is ever caught.
 *
 * A straddling stroke — one whose points span both inside and outside the rectangle — is caught
 * **whole**, not clipped to it: the alternative (splitting a stroke at the rectangle's edge,
 * keeping only the inside portion) would leave a partial, disconnected fragment of somebody's
 * handwriting sitting in the room, which is worse than catching a little more of the mark than
 * was strictly swept over. `boundingRegion` below then measures the caught set's actual extent
 * rather than needing one guessed up front, on both axes.
 *
 * `yRange` is optional, not merely defaulted to "the whole board," because the two calling
 * conventions mean genuinely different things: omitted, this is a pure X-range catch, kept for
 * the pure math this module's own test file already pins with no board or scan line in the
 * picture at all. Given, it is the scan tool's own hard vertical bound — see `ScanTool.ts`'s own
 * doc on why the Y extent stopped being free the moment the scan line gained a user-controlled
 * length (plan/whiteboard.md, "The scan tool").
 */
// spec: SPEC-097
export function strokesInRange(
  strokes: Iterable<StrokeDto>,
  xEdgeA: number,
  xEdgeB: number,
  yRange?: readonly [number, number],
): StrokeDto[] {
  const xLo = Math.min(xEdgeA, xEdgeB);
  const xHi = Math.max(xEdgeA, xEdgeB);
  const yLo = yRange ? Math.min(yRange[0], yRange[1]) : null;
  const yHi = yRange ? Math.max(yRange[0], yRange[1]) : null;
  const caught: StrokeDto[] = [];

  for (const stroke of strokes) {
    const hit = stroke.points.some((p) => {
      if (p.x < xLo || p.x > xHi) {
        return false;
      }

      if (yLo !== null && yHi !== null && (p.y < yLo || p.y > yHi)) {
        return false;
      }

      return true;
    });

    if (hit) {
      caught.push(stroke);
    }
  }

  return caught;
}

/**
 * The bounding box of every point of every stroke in `strokes` — not clipped to whatever
 * range caught them (see `strokesInRange`'s own doc: a straddling stroke is caught whole, and
 * "the region that was replaced" has to cover everything that is about to be deleted, not just
 * the swept sliver a hand happened to sweep through). `null` for no strokes at all — there is no
 * region to replace, and the caller must not call Gemini or delete anything.
 */
export function boundingRegion(strokes: readonly StrokeDto[]): ScanRegion | null {
  if (strokes.length === 0) {
    return null;
  }

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const stroke of strokes) {
    for (const p of stroke.points) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
  }

  if (maxX - minX < MIN_REGION_EXTENT) {
    const midX = (minX + maxX) / 2;
    minX = midX - MIN_REGION_EXTENT / 2;
    maxX = midX + MIN_REGION_EXTENT / 2;
  }

  if (maxY - minY < MIN_REGION_EXTENT) {
    const midY = (minY + maxY) / 2;
    minY = midY - MIN_REGION_EXTENT / 2;
    maxY = midY + MIN_REGION_EXTENT / 2;
  }

  // Every stroke on one board shares a z (WhiteboardLayout: only the north wall is ever the
  // board), so the first point's is as good as any.
  const z = strokes[0]!.points[0]?.z ?? 0;

  return { minX, maxX, minY, maxY, z };
}

/** One replacement stroke's own point list — everything `commitStroke` needs beyond an id,
 *  colour and width, which the caller (`ScanTool.ts`) supplies once for the whole batch. */
export interface LayoutStroke {
  readonly points: Vec3[];
}

/**
 * The per-character ink height to size replacement text against — deliberately **not**
 * `region.maxY - region.minY` (the caught set's own *union* span) once a scan can catch more
 * than one physically separate line of writing at once. `strokesInRange`'s hard Y window can be
 * widened up to `ScanTool.SCAN_LINE_MAX_LENGTH` (close to the full board), specifically so a
 * sweep can catch several stacked lines of ordinary handwriting in one gesture — but the moment
 * it catches two or more lines that sit far apart vertically, `boundingRegion`'s union bbox
 * height stops meaning "how tall is a character here" and starts meaning "how far apart are the
 * lines," which can be many times taller. Sizing replacement text off that union height is what
 * a headset report reproduced as *"scan small handwriting with a long scan line, get the words
 * back at nearly full-board size"* — three small, ordinarily-sized words on three different
 * lines, caught together by a tall scan line, coming back rendered near the union span's own
 * height rather than their own.
 *
 * The median of each **individual caught stroke's own vertical extent** is a much closer proxy
 * for "how tall is one character" than the union span is, and stays close to it for the
 * ordinary case this replaces (a single line, one stroke or a few strokes of similar height,
 * caught together) while no longer exploding when the catch spans multiple separate lines.
 * Median rather than mean so one outlier — a single tall stray mark caught alongside two small
 * words — cannot single-handedly drag the whole line's sizing up the way an average would.
 * Floored at `MIN_REGION_EXTENT` for the same reason `boundingRegion` floors its own extents:
 * a degenerate near-zero height must never reach `layoutTextStrokes`'s own division.
 *
 * `region`'s own extents remain exactly what the image crop and the replacement's *position*
 * are still driven from (`ScanTool.finishScan`) — Gemini needs the full swept area to read
 * everything that was caught, and the replacement still has to start somewhere sensible inside
 * it. Only the *size* of the drawn characters changes to track the ink actually caught rather
 * than the vertical spread between separate pieces of it.
 */
export function characterHeight(strokes: readonly StrokeDto[]): number {
  if (strokes.length === 0) {
    return MIN_REGION_EXTENT;
  }

  const heights = strokes
    .map((stroke) => {
      let minY = Number.POSITIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;

      for (const p of stroke.points) {
        minY = Math.min(minY, p.y);
        maxY = Math.max(maxY, p.y);
      }

      return Math.max(0, maxY - minY);
    })
    .sort((a, b) => a - b);

  const mid = Math.floor(heights.length / 2);
  const median = heights.length % 2 === 1 ? heights[mid]! : (heights[mid - 1]! + heights[mid]!) / 2;

  return Math.max(MIN_REGION_EXTENT, median);
}

/**
 * Lays recognised text out as replacement strokes inside `region`: the first character's left
 * edge sits at `region.minX`, and every character sits on `region.minY` as its baseline —
 * "sized to match the region that was replaced, and sit on that region's own baseline," per the
 * brief this answers. Cap height defaults to `region`'s own vertical extent
 * (`region.maxY - region.minY`) unless `textHeight` is given, in which case that drives the
 * scale instead — see `characterHeight`'s own doc for why the caller (`ScanTool.finishScan`)
 * now always supplies one rather than relying on this default.
 *
 * Multiple transcribed lines (Gemini's own "one line per distinct piece of writing") are joined
 * with a single space into one drawn line, deliberately: laying each line out in its own
 * sub-band would need line-wrapping and per-line height division this tool has no board-width
 * information to do safely (a long single line already just runs past `region.maxX` — untouched
 * by this function, which never wraps or clips, matching `boundingRegion`'s own "do not clip,
 * do not guess" stance; `ScanTool.finishScan` fits the *result* to the board afterward instead,
 * see `fitStrokesToBoard`). A single joined line is legible and simple; multi-line layout is a
 * reasonable improvement to make later, not a correctness requirement of this feature.
 *
 * Never called with an empty or blank `text` — the caller (`ScanTool.finishScan`) already
 * refuses to delete anything or draw a replacement for a failed/empty transcription, which is
 * the property that actually protects someone's handwriting; this function has no opinion of
 * its own about that and simply returns `[]` for blank input as a harmless fallback.
 */
export function layoutTextStrokes(
  text: string,
  region: ScanRegion,
  options: { readonly textHeight?: number } = {},
): LayoutStroke[] {
  const joined = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join(' ');

  if (joined.length === 0) {
    return [];
  }

  const height = options.textHeight ?? region.maxY - region.minY;
  const scale = height / GLYPH_HEIGHT;
  const result: LayoutStroke[] = [];
  let cursor = 0;

  for (const char of joined) {
    const glyph = glyphFor(char);

    if (glyph) {
      for (const strokePoints of glyph) {
        result.push({
          points: strokePoints.map(([gx, gy]) => ({
            x: region.minX + (cursor + gx) * scale,
            y: region.minY + gy * scale,
            z: region.z,
          })),
        });
      }
    }

    cursor += advanceFor(char);
  }

  return result;
}

/** The world-space rectangle the whiteboard's own drawable surface occupies, in the same
 *  room-local x/y a stroke's own points use (`WhiteboardLayout.center`/`width`/`height`,
 *  crossed into plain numbers by `ScanTool.ts` — this module stays Babylon-free, so it takes
 *  the rectangle as data rather than importing `WhiteboardLayout` itself). */
export interface BoardBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/** Floor under either of `fitStrokesToBoard`'s own extents, so a degenerate (near-zero-size)
 *  board or replacement never divides by (near) zero when deriving a scale factor. */
const FIT_EPSILON = 1e-6;

/**
 * Shrinks (never enlarges) and repositions `strokes` so every point lands inside `board` —
 * the independent overflow guard `plan/whiteboard.md`'s own scan tool account does not yet
 * have: nothing about `strokesInRange`, `boundingRegion` or `layoutTextStrokes` has any notion
 * of the board's own edges, so a selection taken near the board's border, or a transcript
 * simply too long for the space it replaces, draws straight past the frame and onto the wall
 * behind it. Deliberately built as its own guard rather than trusted to fixing `characterHeight`
 * above — a *correctly sized* transcript can still be too long to fit if it has enough
 * characters, and "the sizing is right now" is exactly the reasoning that would let that
 * overflow ship unnoticed a second time.
 *
 * **Shrink-to-fit, not wrap or truncate** — the owner's own single-stroke-font reasoning
 * (`StrokeFont.ts`, *"that allows the user to wipe a single character and replace it, then
 * rescan"*) depends on one character mapping to one erasable mark at whatever size it was
 * drawn; wrapping to a second line or truncating would either invent a line break the
 * transcript never had or silently drop characters the model actually read, neither of which a
 * uniform scale-down does. The whole laid-out block is scaled by one factor — both axes
 * together, so the font's own proportions survive — anchored at its own bottom-left corner
 * (where `layoutTextStrokes` already starts a line), then translated only as far as needed to
 * bring it back inside `board`, which also covers a selection that started outside the board's
 * own edges before ever being scaled.
 *
 * A no-op (returns `strokes` unchanged in content, if not in reference) whenever the laid-out
 * block already fits — this only ever shrinks an overflow, never nudges or resizes anything
 * that was already going to land cleanly inside the board.
 */
export function fitStrokesToBoard(
  strokes: readonly LayoutStroke[],
  board: BoardBounds,
): readonly LayoutStroke[] {
  if (strokes.length === 0) {
    return [];
  }

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const stroke of strokes) {
    for (const p of stroke.points) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
  }

  const boardWidth = Math.max(FIT_EPSILON, board.maxX - board.minX);
  const boardHeight = Math.max(FIT_EPSILON, board.maxY - board.minY);
  const contentWidth = Math.max(FIT_EPSILON, maxX - minX);
  const contentHeight = Math.max(FIT_EPSILON, maxY - minY);

  const scale = Math.min(1, boardWidth / contentWidth, boardHeight / contentHeight);

  // Scaling about (minX, minY) keeps that corner fixed, so the scaled block's own extent is
  // exactly [minX, minX + contentWidth * scale] x [minY, minY + contentHeight * scale] — always
  // narrow/short enough (by the scale factor above) to fit inside `board` on both axes at once,
  // so at most one of the two branches per axis below can ever fire.
  const scaledMaxX = minX + contentWidth * scale;
  const scaledMaxY = minY + contentHeight * scale;

  let dx = 0;
  if (minX < board.minX) {
    dx = board.minX - minX;
  } else if (scaledMaxX > board.maxX) {
    dx = board.maxX - scaledMaxX;
  }

  let dy = 0;
  if (minY < board.minY) {
    dy = board.minY - minY;
  } else if (scaledMaxY > board.maxY) {
    dy = board.maxY - scaledMaxY;
  }

  if (scale === 1 && dx === 0 && dy === 0) {
    return strokes;
  }

  return strokes.map((stroke) => ({
    points: stroke.points.map((p) => ({
      x: minX + (p.x - minX) * scale + dx,
      y: minY + (p.y - minY) * scale + dy,
      z: p.z,
    })),
  }));
}
